import {
  AssociateAutoMessageBody,
  BookingDeclineAttachment,
  BookingOfferAttachment,
  BookingRequestAttachment,
  ConversationAttachment,
  CreateAutoMessageBody,
  FileConversationAttachment,
  ListingConversationAttachment,
  PostSupplierMessageBody,
  PurchaseConversationAttachment,
  UpdateAutoMessageBody,
  UploadMessageAttachmentBody,
} from "@givenwell/management-api";
import { infiniteQueryOptions, queryOptions, useMutation } from "@tanstack/react-query";
import { api, queryClient } from "./utils";

export const conversationsQueryKey = ["conversations"];
export const conversationsListQueryKey = [...conversationsQueryKey, "list"];

export const conversationsQuery = (supplierId: string) =>
  queryOptions({
    queryKey: [...conversationsListQueryKey, supplierId],
    queryFn: async () => {
      const res = await api.mgmtMessages.getSupplierConversations({ supplierId });
      return res.conversations || [];
    },
  });

export const conversationQuery = (supplierId: string, conversationId: string) =>
  queryOptions({
    queryKey: [...conversationsQueryKey, conversationId, supplierId],
    queryFn: async () => {
      const res = await api.mgmtMessages.getSupplierConversations({ supplierId });
      const conversations = res.conversations || [];
      return conversations.find(c => c.id === conversationId) || null;
    },
  });

type ChatPageParam = {
  before?: string;
  after?: string;
};

export const conversationMessagesQuery = (supplierId: string, conversationId: string) =>
  infiniteQueryOptions({
    queryKey: [...conversationsQueryKey, conversationId, "messages", supplierId],
    retry: false,
    queryFn: async ({ pageParam }) => {
      const res = await api.mgmtMessages.getSupplierConversationMessages({
        supplierId,
        conversationId,
        beforeThisMessageId: pageParam.before,
        afterThisMessageId: pageParam.after,
      });
      return res.messages;
    },
    initialPageParam: {
      before: undefined,
      after: undefined,
    } as ChatPageParam,
    getNextPageParam: (lastPage, allPages) => {
      const allMessages = allPages.flat();
      if (allMessages.length === 0) {
        return {
          before: undefined,
          after: undefined,
        };
      }
      const lastMessage = allMessages[allMessages.length - 1];
      return {
        before: undefined,
        after: lastMessage?.id,
      };
    },
    getPreviousPageParam: firstPage => {
      if (firstPage.length === 0) {
        return;
      }
      const firstMessage = firstPage[0];
      return {
        before: firstMessage?.id,
        after: undefined,
      };
    },
  });

// Mutations

export function usePostSupplierMessageMutation(conversationId: string) {
  return useMutation({
    mutationFn: async (requestBody: PostSupplierMessageBody) => {
      return await api.mgmtMessages.postSupplierMessage({ conversationId, requestBody });
    },
  });
}

export function useMarkMessageSeenMutation(conversationId: string) {
  return useMutation({
    mutationFn: async (messageId: string) => {
      return await api.mgmtMessages.markSupplierMessageSeen({ messageId });
    },
    onSuccess: (data, messageId) => {
      queryClient.setQueryData(conversationMessagesQuery("idk", conversationId).queryKey, oldData => {
        if (!oldData) {
          return oldData;
        }
        const newPages = oldData.pages.map(page => {
          return page.map(message => {
            if (message.id === messageId) {
              return {
                ...message,
                seen: true,
              };
            }
            return message;
          });
        });
        return {
          ...oldData,
          pages: newPages,
        };
      });
      queryClient.invalidateQueries({
        queryKey: conversationsQueryKey,
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: conversationsListQueryKey,
      });
    },
  });
}

export function useUploadMessageAttachmentMutation(supplierId: string, conversationId: string) {
  return useMutation({
    mutationFn: async (body: UploadMessageAttachmentBody) => {
      return await api.mgmtMessages.uploadMessageAttachment({ supplierId, conversationId, requestBody: body });
    },
  });
}

export function useDownloadMessageAttachmentMutation(supplierId: string) {
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      return await api.mgmtMessages.downloadMessageAttachmentAsSupplier({ supplierId, attachmentId });
    },
    onSuccess: () => {},
  });
}

// Automated Messages

export const autoMessagesQuery = (supplierId: string) =>
  queryOptions({
    queryKey: ["auto-messages", "list", supplierId],
    queryFn: async () => {
      const res = await api.mgmtMessages.getAutoMessages({ supplierId });
      return res.auto_messages;
    },
  });

export function useCreateAutoMessageMutation(supplierId?: string) {
  return useMutation({
    mutationFn: async (body: CreateAutoMessageBody) => {
      return await api.mgmtMessages.createAutoMessage({ supplierId: supplierId!, requestBody: body });
    },
  });
}

export function useUpdateAutoMessageMutation(supplierId: string, autoId: string) {
  return useMutation({
    mutationFn: async (body: UpdateAutoMessageBody) => {
      return await api.mgmtMessages.updateAutoMessage({ supplierId, autoId, requestBody: body });
    },
  });
}

export function useAssociateAutoMessageMutation(supplierId: string, listingId: string, listingVersion: number) {
  return useMutation({
    mutationFn: async (body: AssociateAutoMessageBody) => {
      return await api.mgmtMessages.associateAutoMessage({ supplierId, listingId, listingVersion, requestBody: body });
    },
  });
}

export function useDismissMessageMutation() {
  return useMutation({
    mutationFn: async (messageId: string) => {
      return await api.mgmtMessages.markMessageResponseRequired({
        messageId,
        requestBody: {
          response_required: false,
        },
      });
    },
  });
}

// Typescript Utils

export function isPurchaseAttachment(attachment: ConversationAttachment): attachment is PurchaseConversationAttachment {
  return attachment.$type === "purchase";
}
export function isFileAttachment(attachment: ConversationAttachment): attachment is FileConversationAttachment {
  return attachment.$type === "file";
}
export function isListingAttachment(attachment: ConversationAttachment): attachment is ListingConversationAttachment {
  return attachment.$type === "listing";
}
export function isBookingRequestAttachment(attachment: ConversationAttachment): attachment is BookingRequestAttachment {
  return attachment.$type === "booking_request";
}
export function isBookingOfferAttachment(attachment: ConversationAttachment): attachment is BookingOfferAttachment {
  return attachment.$type === "booking_offer";
}
export function isBookingDeclineAttachment(attachment: ConversationAttachment): attachment is BookingDeclineAttachment {
  return attachment.$type === "booking_decline";
}
